// frontend/src/components/StoryIdentityPlate.js
// Build: 2026-02-19-story-identity-plate-v2 (final)
//
// Purpose:
// - Consistent “NextScenes-ish” identity tile (forum band + title + author + meta)
// - Used by Storylines, shelves, StoryHub, etc.
// - Stops duplication: the plate carries identity once, the card carries actions/dates.
//
// Notes:
// - No dependency on StoryCover
// - Safe with story.id or story._id
// - Inline styles only (no global CSS edits)
// - Optional click handlers for Story and Author

import React, { useMemo } from "react";

function safeStr(v) {
  return typeof v === "string" ? v : "";
}

function safeTrim(v) {
  return String(v || "").trim();
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function getStoryId(story) {
  return safeTrim(story?._id || story?.id || "");
}

function getAuthorInfo(story) {
  const authorObj =
    (story?.authorProfile && typeof story.authorProfile === "object" ? story.authorProfile : null) ||
    (story?.author && typeof story.author === "object" ? story.author : null);

  const authorString = typeof story?.author === "string" ? story.author : "";

  const authorName =
    safeTrim(authorObj?.name) ||
    safeTrim(story?.authorName) ||
    safeTrim(story?.ownerName) ||
    safeTrim(story?.createdByName) ||
    "";

  const authorUsername =
    safeTrim(authorObj?.username) ||
    safeTrim(story?.authorUsername) ||
    safeTrim(story?.ownerUsername) ||
    safeTrim(story?.createdByUsername) ||
    safeTrim(authorString) ||
    safeTrim(story?.createdBy) ||
    "";

  const avatarUrl =
    safeTrim(authorObj?.avatarUrl) ||
    safeTrim(story?.authorAvatarUrl) ||
    safeTrim(story?.authorAvatar) ||
    safeTrim(story?.ownerAvatarUrl) ||
    safeTrim(story?.ownerAvatar) ||
    "";

  const displayName = authorName || (authorUsername ? authorUsername : "Unknown author");
  const handle = authorUsername ? `@${authorUsername}` : "";

  return { authorName, authorUsername, avatarUrl, displayName, handle };
}

export function forumBandColor(forumRaw) {
  const f = String(forumRaw || "").toLowerCase();
  if (f.includes("mystery")) return "#0ea5e9";
  if (f.includes("crime")) return "#ef4444";
  if (f.includes("fantasy")) return "#a855f7";
  if (f.includes("romance")) return "#f43f5e";
  if (f.includes("poetry")) return "#22c55e";
  if (f.includes("science")) return "#38bdf8";
  if (f.includes("historical")) return "#f59e0b";
  if (f.includes("children")) return "#10b981";
  if (f.includes("non-fiction") || (f.includes("non") && f.includes("fiction"))) return "#16a34a";
  if (f.includes("humour") || f.includes("humor")) return "#f97316";
  if (f.includes("thriller") || f.includes("suspense")) return "#111827";
  if (f.includes("faith") || f.includes("inspiration")) return "#0b703d";
  return "#0f766e";
}

function clampInt(n, min, max, fallback) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  const v = Math.floor(x);
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

function inferCanonCount(story) {
  const c = story?.canonCount ?? story?.canonScenes ?? story?.canonApprovedCount ?? story?.canonApproved ?? null;

  const n = Number(c);
  if (Number.isFinite(n)) return Math.max(0, Math.floor(n));

  // fallback: count approved/canon scenes if present
  const scenes = safeArray(story?.scenes);
  const approved = scenes.filter((s) => s && (s.isCanon === true || s.status === "canon" || s.approved === true));
  return approved.length;
}

function initialsFromName(name) {
  const n = safeTrim(name);
  if (!n) return "?";
  const parts = n.split(" ").filter(Boolean).slice(0, 2);
  const letters = parts.map((p) => (p[0] ? p[0].toUpperCase() : "")).filter(Boolean);
  return letters.join("") || n[0].toUpperCase();
}

export default function StoryIdentityPlate({
  story,
  width = 140,
  height = 176,
  showFooter = true,
  footerLabel = "A NextScenes Story",

  // Optional UI toggles
  showAvatar = false,
  showHandle = false,
  showCanonInMeta = true,

  // Optional interactions
  onOpenStory, // (storyId, story) => void
  onOpenAuthor, // (authorUsername, story) => void
}) {
  const id = useMemo(() => getStoryId(story), [story]);

  const title = useMemo(() => safeTrim(story?.title) || "Untitled Story", [story]);
  const author = useMemo(() => getAuthorInfo(story), [story]);

  const band = useMemo(() => forumBandColor(story?.forum), [story]);

  const forumLabel = useMemo(() => safeTrim(story?.forum).replace(/\s+Forum$/i, "").trim(), [story]);
  const modeLabel = useMemo(() => safeTrim(story?.writingMode), [story]);
  const canonCount = useMemo(() => inferCanonCount(story), [story]);

  const titleShort = title.length > 42 ? title.slice(0, 42) + "…" : title;
  const authorShort =
    author.displayName.length > 28 ? author.displayName.slice(0, 28) + "…" : author.displayName;

  const metaLine = useMemo(() => {
    const parts = [];
    if (forumLabel) parts.push(forumLabel);
    if (modeLabel) parts.push(modeLabel);
    if (showCanonInMeta && canonCount > 0) parts.push(`${clampInt(canonCount, 0, 999999, 0)} canon`);
    return parts.join(" • ");
  }, [forumLabel, modeLabel, canonCount, showCanonInMeta]);

  const clickableStory = typeof onOpenStory === "function" && Boolean(id);
  const clickableAuthor = typeof onOpenAuthor === "function" && Boolean(author.authorUsername);

  function handleStoryClick(e) {
    if (!clickableStory) return;
    if (e?.preventDefault) e.preventDefault();
    onOpenStory(id, story);
  }

  function handleAuthorClick(e) {
    if (!clickableAuthor) return;
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    onOpenAuthor(author.authorUsername, story);
  }

  return (
    <div
      className="ns-story-identity-plate"
      aria-label="Story identity"
      data-story-id={id || undefined}
      title={title}
      onClick={handleStoryClick}
      role={clickableStory ? "button" : undefined}
      tabIndex={clickableStory ? 0 : undefined}
      onKeyDown={(e) => {
        if (!clickableStory) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenStory(id, story);
        }
      }}
      style={{
        width,
        minWidth: width,
        height,
        borderRadius: 14,
        overflow: "hidden",
        background: "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 100%)",
        border: "1px solid rgba(15,23,42,0.10)",
        boxShadow: "0 8px 20px rgba(2,6,23,0.08)",
        display: "flex",
        flexDirection: "column",
        cursor: clickableStory ? "pointer" : "default",
        userSelect: "none",
      }}
    >
      <div style={{ height: 12, background: band }} />

      <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            fontWeight: 900,
            color: "#ffffff",
            fontSize: 12,
            lineHeight: "14px",
            letterSpacing: 0.2,
            textTransform: "uppercase",
            opacity: 0.96,
          }}
        >
          {titleShort}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {showAvatar ? (
            author.avatarUrl ? (
              <img
                src={author.avatarUrl}
                alt={author.displayName}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  objectFit: "cover",
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.10)",
                  flex: "0 0 auto",
                }}
                onError={(e) => {
                  // fallback to initials if avatar fails
                  try {
                    e.currentTarget.style.display = "none";
                  } catch {}
                }}
              />
            ) : (
              <div
                aria-hidden="true"
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "rgba(255,255,255,0.86)",
                  fontSize: 10,
                  fontWeight: 900,
                  flex: "0 0 auto",
                }}
                title={author.displayName}
              >
                {initialsFromName(author.displayName)}
              </div>
            )
          ) : null}

          {clickableAuthor ? (
            <button
              type="button"
              onClick={handleAuthorClick}
              title={`View ${author.handle || author.displayName}`}
              aria-label={`View ${author.handle || author.displayName}`}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                margin: 0,
                textAlign: "left",
                cursor: "pointer",
                color: "rgba(255,255,255,0.78)",
                fontSize: 11,
                lineHeight: "13px",
                textDecoration: "underline",
                textUnderlineOffset: 3,
                fontWeight: 850,
              }}
            >
              {authorShort}
              {showHandle && author.handle ? (
                <span style={{ color: "rgba(255,255,255,0.62)", fontWeight: 800 }}> {" "}{author.handle}</span>
              ) : null}
            </button>
          ) : (
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11, lineHeight: "13px" }}>
              {authorShort}
              {showHandle && author.handle ? (
                <span style={{ color: "rgba(255,255,255,0.62)", fontWeight: 800 }}> {" "}{author.handle}</span>
              ) : null}
            </div>
          )}
        </div>

        {metaLine ? (
          <div style={{ marginTop: 6, color: "rgba(255,255,255,0.68)", fontSize: 11, lineHeight: "13px" }}>
            {metaLine}
          </div>
        ) : (
          <div style={{ marginTop: 6, color: "rgba(255,255,255,0.55)", fontSize: 11, lineHeight: "13px" }}>
            Storyline
          </div>
        )}
      </div>

      {showFooter ? (
        <div style={{ marginTop: "auto", padding: 10 }}>
          <div
            style={{
              height: 32,
              borderRadius: 12,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255,255,255,0.80)",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.2,
            }}
          >
            {footerLabel}
          </div>
        </div>
      ) : null}
    </div>
  );
}
